import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Crown, X } from 'lucide-react';

/**
 * WinbackVipBanner
 * Floating banner shown at the top of every page (below the cookie banner /
 * offline notice) ONLY when the logged-in user has a temporary VIP grant
 * issued by a Win-Back campaign. The countdown updates live every minute.
 *
 * Dismissible via the X button (per-session, not persisted) so it doesn't
 * cover the UI permanently. Re-appears at next app open.
 */
const fmtRemaining = (ms) => {
  if (ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d >= 1) return `${d}gg ${h}h`;
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function WinbackVipBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [, setTick] = useState(0);  // forces re-render every 60s

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!user || dismissed) return null;
  if (user.vip_grant_source !== 'winback') return null;
  if (!user.vip_granted_until) return null;

  const expiresAt = new Date(user.vip_granted_until);
  if (Number.isNaN(expiresAt.getTime())) return null;
  const remaining = expiresAt.getTime() - Date.now();
  const label = fmtRemaining(remaining);
  if (!label) return null;  // already expired

  return (
    <div
      className="sticky top-0 z-40 mx-2 mt-2 rounded-xl px-3 py-2.5 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 shadow-lg shadow-amber-500/20 border border-amber-300/30 flex items-center gap-3"
      data-testid="winback-vip-banner"
    >
      <Crown className="w-5 h-5 text-yellow-100 shrink-0 drop-shadow" />
      <div className="flex-1 min-w-0 text-[12.5px] leading-tight text-black">
        <div className="font-black uppercase tracking-wide">VIP gratuito attivo</div>
        <div className="text-[11px] text-black/80 mt-0.5">
          Ti scade tra <b className="text-black">{label}</b> — sfrutta i pronostici premium!
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Chiudi banner"
        className="text-black/60 hover:text-black p-1 rounded-md hover:bg-black/10 shrink-0"
        data-testid="winback-vip-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
